import type { TrackingTrace } from '../perf/TrackingTrace';
import type { FrameFit } from '../config/frames';
import type { OccluderParams } from '../render/HeadOccluder';
import type { QualityGovernor } from '../perf/QualityGovernor';
import { QUALITY_LEVELS } from '../perf/QualityGovernor';
import type { FramePoseSolver } from '../pose/FramePoseSolver';
import type { RendererStats } from '../render/Renderer';
import type { PoseSmoother } from '../smoothing/PoseSmoother';

export interface DebugStats {
  fps: number;
  inferMs: number;
  inferP95Ms: number;
  inferPerSecond: number;
  latencyMs: number;
  mode: string;
  delegate: string;
  state: string;
  hint: string;
  yaw: number;
  pitch: number;
  roll: number;
  depthMm: number;
  irisPx: number;
  irisReliable: boolean;
  pdMm: number | null;
  faceWidthMm: number | null;
  headWidthMm: number | null;
  calibrated: boolean;
  quality: string;
  spreadDeg: number;
  still: boolean;
  coasting: boolean;
  predictedMs: number;
  losses: number;
  lastReacquireMs: number;
  videoSize: string;
  inferSize: string;
  /** RMS jitter (px) of the rendered anchor over the last ~1 s, and of the raw solver output. */
  jitterPx: number;
  rawJitterPx: number;
  /** Iris / MediaPipe depth ratio (scale calibration), null until calibrated. */
  depthScale: number | null;
  /** Depth extent of the face-mesh occluder, nose tip to ear front (mm) — ~100 on an adult. */
  meshDepthMm: number | null;
  /** Factor applied to MediaPipe's depth offsets to reach that extent (1 = raw). */
  meshDepthCorrection: number | null;
  /** Landmarks the position anchor is fitted over (2 = canthi fallback) and their scatter (mm). */
  anchorPoints: number | null;
  anchorSpreadMm: number | null;
  /** Display locked to the tracked frame (no prediction) rather than the live video. */
  frameLock: boolean;
  displayFps: number;
  cameraFps: number | null;
  drawnFrames: number;
  displayedFrameAgeMs: number | null;
  /** A110Y: WebGL resource counters (must stay flat across frames and acetates), restarts and context losses. */
  gl: RendererStats | null;
  restarts: number;
  contextLosses: number;
}

export interface DebugBindings {
  smoother: PoseSmoother;
  solver: FramePoseSolver;
  governor: QualityGovernor;
  getOccluder(): OccluderParams;
  patchOccluder(patch: Partial<OccluderParams>): void;
  setShowLandmarks(enabled: boolean): void;
  setNumFaces(n: number): void;
  patchFit(patch: Partial<FrameFit>): void;
  getFit(): FrameFit;
  getStats(): DebugStats;
  /** Resolves to a PNG data URL of video + overlay (acceptance protocol evidence). */
  capture(): Promise<string | null>;
  setCameraFov(degrees: number): void;
  setPhysicalLenses(enabled: boolean): void;
  getPhysicalLenses(): boolean;
  startTrace(): void;
  stopTrace(): TrackingTrace | null;
}

/**
 * Live tuning panel (open with ?debug=1, the "d" key, or a long press on the
 * product name). Exposes the One Euro parameters, the fit offsets and the FOV
 * assumption, plus a "copy" button that serialises the current values so a
 * tuning session on a real device can be pasted back into config.
 */
export class DebugPanel {
  private readonly host: HTMLElement;
  private readonly b: DebugBindings;
  private readonly statsEl: HTMLPreElement;
  private visible = false;

  constructor(host: HTMLElement, bindings: DebugBindings) {
    this.host = host;
    this.b = bindings;
    host.innerHTML = '';

    this.statsEl = document.createElement('pre');
    host.append(this.section('Stats'), this.statsEl);

    host.append(this.checkbox('Physical lens absorption', () => this.b.getPhysicalLenses(), (v) => this.b.setPhysicalLenses(v)));
    host.append(this.section('Position (One Euro)'));
    const sp = () => this.b.smoother.getParams();
    host.append(
      this.slider('minCutoff', 0.1, 5, 0.05, () => sp().position.minCutoff, (v) => this.b.smoother.setParams({ position: { ...sp().position, minCutoff: v } })),
      this.slider('beta', 0, 0.3, 0.001, () => sp().position.beta, (v) => this.b.smoother.setParams({ position: { ...sp().position, beta: v } })),
    );
    host.append(this.section('Depth (One Euro)'));
    host.append(
      this.slider('minCutoff', 0.1, 3, 0.05, () => sp().depth.minCutoff, (v) => this.b.smoother.setParams({ depth: { ...sp().depth, minCutoff: v } })),
      this.slider('beta', 0, 0.05, 0.001, () => sp().depth.beta, (v) => this.b.smoother.setParams({ depth: { ...sp().depth, beta: v } })),
    );
    host.append(this.section('Rotation (One Euro)'));
    host.append(
      this.slider('minCutoff', 0.1, 5, 0.05, () => sp().rotation.minCutoff, (v) => this.b.smoother.setParams({ rotation: { ...sp().rotation, minCutoff: v } })),
      this.slider('beta', 0, 8, 0.01, () => sp().rotation.beta, (v) => this.b.smoother.setParams({ rotation: { ...sp().rotation, beta: v } })),
    );
    host.append(this.section('Stillness / prediction'));
    host.append(
      this.checkbox('stillness lock', () => sp().stillness.enabled, (v) => this.b.smoother.setParams({ stillness: { ...sp().stillness, enabled: v } })),
      this.slider('lock scale', 0.1, 1, 0.05, () => sp().stillness.cutoffScale, (v) => this.b.smoother.setParams({ stillness: { ...sp().stillness, cutoffScale: v } })),
      this.checkbox('prediction', () => sp().prediction.enabled, (v) => this.b.smoother.setParams({ prediction: { ...sp().prediction, enabled: v } })),
      this.slider('predict gain', 0, 1, 0.05, () => sp().prediction.gain, (v) => this.b.smoother.setParams({ prediction: { ...sp().prediction, gain: v } })),
      this.checkbox('lag compensation', () => sp().lagCompensation.enabled, (v) => this.b.smoother.setParams({ lagCompensation: { ...sp().lagCompensation, enabled: v } })),
      this.slider('lag gain', 0, 1, 0.05, () => sp().lagCompensation.gain, (v) => this.b.smoother.setParams({ lagCompensation: { ...sp().lagCompensation, gain: v } })),
    );

    host.append(this.section('Camera / fit'));
    host.append(
      this.slider('FOV long side °', 45, 95, 1, () => this.b.solver.options.longSideFovDeg, (v) => this.b.setCameraFov(v)),
      this.slider('vertex mm', 4, 30, 0.5, () => this.b.getFit().vertexDistanceMm, (v) => this.b.patchFit({ vertexDistanceMm: v })),
      this.slider('offset Y mm', -12, 12, 0.5, () => this.b.getFit().anchorOffsetYMm, (v) => this.b.patchFit({ anchorOffsetYMm: v })),
      this.slider('pantoscopic °', 0, 16, 0.5, () => this.b.getFit().pantoscopicTiltDeg, (v) => this.b.patchFit({ pantoscopicTiltDeg: v })),
      this.checkbox('show landmarks', () => false, (v) => this.b.setShowLandmarks(v)),
    );

    host.append(this.section('Occlusion'));
    const oc = () => this.b.getOccluder();
    host.append(
      this.checkbox('occlusion', () => oc().enabled, (v) => this.b.patchOccluder({ enabled: v })),
      this.checkbox('face mesh', () => oc().faceMesh, (v) => this.b.patchOccluder({ faceMesh: v })),
      this.checkbox('head + ears', () => oc().head, (v) => this.b.patchOccluder({ head: v })),
      this.slider('face bias mm', -5, 10, 0.5, () => oc().faceBiasMm, (v) => this.b.patchOccluder({ faceBiasMm: v })),
      this.slider('head margin mm', -8, 8, 0.5, () => oc().headMarginMm, (v) => this.b.patchOccluder({ headMarginMm: v })),
      this.slider('shadow', 0, 0.8, 0.01, () => oc().shadowOpacity, (v) => this.b.patchOccluder({ shadowOpacity: v })),
    );

    host.append(this.section('Quality'));
    const select = document.createElement('select');
    QUALITY_LEVELS.forEach((l, i) => {
      const o = document.createElement('option');
      o.value = String(i);
      o.textContent = `${l.name} (${l.inferLongSide}px, dpr≤${l.pixelRatioCap})`;
      select.append(o);
    });
    select.value = String(this.b.governor.levelIndex);
    select.onchange = () => {
      this.b.governor.params.locked = true;
      this.b.governor.setLevel(Number(select.value), 'manual');
    };
    this.b.governor.onChange(() => {
      select.value = String(this.b.governor.levelIndex);
    });
    const lockLabel = this.checkbox('lock level', () => this.b.governor.params.locked, (v) => (this.b.governor.params.locked = v));
    const facesLabel = this.checkbox('track 2 faces', () => false, (v) => this.b.setNumFaces(v ? 2 : 1));
    host.append(select, lockLabel, facesLabel);

    const copy = document.createElement('button');
    copy.textContent = 'Copy params';
    copy.onclick = () => {
      const payload = { smoother: this.b.smoother.getParams(), fit: this.b.getFit(), longSideFovDeg: this.b.solver.options.longSideFovDeg, occluder: this.b.getOccluder() };
      const text = JSON.stringify(payload, null, 2);
      void navigator.clipboard?.writeText(text);
      console.info('[tryon:params]\n' + text);
    };
    const capture = document.createElement('button');
    capture.textContent = 'Capture PNG';
    capture.style.marginLeft = '6px';
    capture.onclick = () => {
      void this.b.capture().then((url) => {
        if (!url) return;
        const a = document.createElement('a');
        a.href = url;
        a.download = `mata-tryon-${Date.now()}.png`;
        a.click();
      });
    };
    host.append(this.section('Export'), copy, capture);
    const record = document.createElement('button'); record.textContent = 'Record landmarks locally (30 s max)';
    const save = document.createElement('button'); save.textContent = 'Stop + save local trace'; save.disabled = true;
    record.onclick = () => { this.b.startTrace(); record.disabled = true; save.disabled = false; };
    save.onclick = () => {
      const trace = this.b.stopTrace(); record.disabled = false; save.disabled = true;
      if (!trace) return;
      const url = URL.createObjectURL(new Blob([JSON.stringify(trace)], { type: 'application/json' }));
      const a = document.createElement('a'); a.href = url; a.download = 'mata-tracking-trace.json'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    host.append(record, save);
  }

  update(): void {
    if (!this.visible) return;
    const s = this.b.getStats();
    const f = (n: number | null, d = 1) => (n === null ? '—' : n.toFixed(d));
    this.statsEl.textContent =
      `fps ${f(s.fps, 0)}  infer ${f(s.inferMs)} ms (p95 ${f(s.inferP95Ms)})  ${s.inferPerSecond}/s\n` +
      `camera ${f(s.cameraFps, 0)}/s  shown ${s.displayFps}/s  image age ${f(s.displayedFrameAgeMs)} ms\n` +
      `capture→result ${f(s.latencyMs)} ms  predicted ${f(s.predictedMs)} ms\n` +
      `${s.mode}/${s.delegate}  q:${s.quality}  video ${s.videoSize}  infer ${s.inferSize}\n` +
      (s.gl ? `gl tex ${s.gl.textures}  geo ${s.gl.geometries}  prog ${s.gl.programs}  calls ${s.gl.calls}  buf ${(s.gl.bufferPixels / 1e6).toFixed(1)} MP  restarts ${s.restarts}  ctx lost ${s.contextLosses}\n` : '') +
      `state ${s.state}${s.coasting ? ' (coast)' : ''}${s.still ? ' still' : ''}  hint ${s.hint}\n` +
      `yaw ${f(s.yaw)}  pitch ${f(s.pitch)}  roll ${f(s.roll)}\n` +
      `depth ${f(s.depthMm, 0)} mm  iris ${f(s.irisPx)} px ${s.irisReliable ? 'ok' : 'gated'}  cal ${s.calibrated ? 'yes' : 'no'}\n` +
      `PD ${f(s.pdMm)}  face ${f(s.faceWidthMm, 0)}  head ${f(s.headWidthMm, 0)} mm  spread ${f(s.spreadDeg)}°\n` +
      `jitter ${f(s.jitterPx, 2)} px (raw ${f(s.rawJitterPx, 2)})  losses ${s.losses}  reacquire ${f(s.lastReacquireMs, 0)} ms\n` +
      `depth scale ${f(s.depthScale, 3)}  mesh nose→ear ${f(s.meshDepthMm, 0)} mm (z ×${f(s.meshDepthCorrection, 2)})\n` +
      `anchor ${s.anchorPoints ?? '—'} pts  scatter ${f(s.anchorSpreadMm, 2)} mm  display ${s.frameLock ? 'frame-locked' : 'live+prediction'}`;
  }

  show(): void {
    this.visible = true;
    this.host.hidden = false;
  }

  hide(): void {
    this.visible = false;
    this.host.hidden = true;
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  get isVisible(): boolean {
    return this.visible;
  }

  private section(title: string): HTMLElement {
    const h = document.createElement('h4');
    h.textContent = title;
    return h;
  }

  private slider(label: string, min: number, max: number, step: number, get: () => number, set: (v: number) => void): HTMLElement {
    const wrap = document.createElement('label');
    const span = document.createElement('span');
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(get());
    const refresh = () => (span.textContent = `${label} ${Number(input.value).toFixed(step < 0.01 ? 3 : step < 0.1 ? 2 : 1)}`);
    input.oninput = () => {
      set(Number(input.value));
      refresh();
    };
    refresh();
    wrap.append(span, input);
    return wrap;
  }

  private checkbox(label: string, get: () => boolean, set: (v: boolean) => void): HTMLElement {
    const wrap = document.createElement('label');
    const span = document.createElement('span');
    span.textContent = label;
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = get();
    input.onchange = () => set(input.checked);
    wrap.append(span, input);
    return wrap;
  }
}
